import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useSchoolBrand } from '../../hooks/useSchoolBrand';
import { useSchoolModules } from '../../hooks/useSchoolModules';
import {
  HomeIcon, UserGroupIcon, AcademicCapIcon, InboxStackIcon, FingerPrintIcon,
  BanknotesIcon, CalendarDaysIcon, BookOpenIcon, HomeModernIcon,
  MegaphoneIcon, Cog6ToothIcon, ClipboardDocumentCheckIcon,
  ChartBarIcon, DocumentChartBarIcon, UsersIcon, TableCellsIcon, XMarkIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon, CubeIcon, ShieldCheckIcon,
  WrenchScrewdriverIcon, BriefcaseIcon,
  ArrowRightOnRectangleIcon, NoSymbolIcon, CreditCardIcon, TrashIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard',             href: '/dashboard',             icon: HomeIcon },
  { name: 'Students',              href: '/students',              icon: UserGroupIcon },
  { name: 'Left Students',         href: '/student-left',          icon: ArrowRightOnRectangleIcon },
  { name: 'Teachers',              href: '/teachers',              icon: UsersIcon },
  { name: 'Staff Exits',           href: '/staff-exits',           icon: NoSymbolIcon },
  { name: 'Coordinators',          href: '/coordinators',          icon: BriefcaseIcon },
  { name: 'Finance Members',       href: '/finance-members',       icon: CurrencyDollarIcon },
  { name: 'Parent Accounts',       href: '/parent-accounts',       icon: UserGroupIcon },
  { name: 'Classes',               href: '/classes',               icon: AcademicCapIcon },
  { name: 'Timetable',             href: '/timetable',             icon: TableCellsIcon },
  { name: 'Teacher Timetable',      href: '/teacher-timetable',     icon: TableCellsIcon },
  { name: 'Datesheets',            href: '/datesheets',            icon: TableCellsIcon },
  { name: 'Assessment Types',      href: '/assessment-types',      icon: ChartBarIcon },
  { name: 'Terms',                 href: '/term-periods',          icon: CalendarDaysIcon },
  { name: 'Subject Split',         href: '/term-subject-split',    icon: CalendarDaysIcon },
  { name: 'Term Exams',            href: '/term-exams',            icon: ClipboardDocumentListIcon },
  { name: 'Assessments',           href: '/assessments',           icon: ClipboardDocumentCheckIcon },
  { name: 'Grade Reports',         href: '/grade-reports',         icon: DocumentChartBarIcon },
  { name: 'Class Promotion',       href: '/class-promotion',       icon: AcademicCapIcon },
  { name: 'Year-End Cleanup',      href: '/year-end-cleanup',      icon: TrashIcon },
  { name: 'ILP',                   href: '/ilp',                   icon: ClipboardDocumentListIcon },
  { name: 'CLO Management',        href: '/clo-management',        icon: ChartBarIcon },
  { name: 'CLO Attainment',        href: '/attainment',            icon: ChartBarIcon },
  { name: 'PLO Management',        href: '/plo-management',        icon: ChartBarIcon },
  { name: 'PLO Attainment',        href: '/plo-attainment',        icon: ChartBarIcon },
  { name: 'Student Attendance',    href: '/student-attendance',    icon: CalendarDaysIcon },
  { name: 'Staff Attendance',      href: '/staff-attendance',      icon: CalendarDaysIcon },
  { name: 'Teacher Attendance',    href: '/teacher-attendance',    icon: CalendarDaysIcon },
  { name: 'Biometric',             href: '/biometric',             icon: FingerPrintIcon },
  { name: 'Student Gate Log',      href: '/student-gate-log',      icon: CreditCardIcon },
  { name: 'Staff Salary',          href: '/staff-salary',          icon: ChartBarIcon },
  { name: 'Staff Leaves',          href: '/staff-leaves',          icon: ClipboardDocumentCheckIcon },
  { name: 'Staff Management (Non-Portal)', href: '/staff-management', icon: WrenchScrewdriverIcon },
  { name: 'Holidays',              href: '/holidays',              icon: CalendarDaysIcon },
  { name: 'Salary Records',        href: '/salary-records',        icon: ChartBarIcon },
  { name: 'Leaves',                href: '/leaves',                icon: ClipboardDocumentCheckIcon },
  { name: 'Fees',                  href: '/fees',                  icon: BanknotesIcon },
  { name: 'Expenses',              href: '/expenses',              icon: CurrencyDollarIcon },
  { name: 'Assets',                href: '/assets',                icon: CubeIcon },
  { name: 'Materials',             href: '/materials',             icon: BookOpenIcon },
  { name: 'Certificates',          href: '/certificates',          icon: AcademicCapIcon },
  { name: 'Notices',               href: '/notices',               icon: MegaphoneIcon,         badge: 'notices' },
  { name: 'Feedback',              href: '/feedback',              icon: DocumentChartBarIcon },
  { name: 'Access Panel',          href: '/access',                icon: ShieldCheckIcon },
  { name: 'Coordinator Requests',  href: '/coordinator-requests',  icon: InboxStackIcon,        badge: 'coordinator' },
  { name: 'Finance Requests',      href: '/finance-requests',      icon: BanknotesIcon,         badge: 'finance' },
  { name: 'Settings',              href: '/settings',              icon: Cog6ToothIcon },
  { name: 'My Attendance',         href: '/my-attendance',         icon: CalendarDaysIcon },
  { name: 'My Salary',             href: '/my-salary',             icon: ChartBarIcon },
  { name: 'My Leaves',             href: '/my-leaves',             icon: ClipboardDocumentCheckIcon },
];

const sections = [
  { label: 'Main',         items: navigation.filter(n => ['Dashboard'].includes(n.name)) },
  { label: 'Academic',     items: navigation.filter(n => ['Students','Left Students','Teachers','Staff Exits','Coordinators','Finance Members','Parent Accounts','Classes','Timetable','Teacher Timetable','Datesheets','Assessment Types','Terms','Subject Split','Term Exams','Assessments','Grade Reports','Class Promotion','Year-End Cleanup','ILP'].includes(n.name)) },
  { label: 'OBE',          items: navigation.filter(n => ['CLO Management','CLO Attainment','PLO Management','PLO Attainment'].includes(n.name)) },
  { label: 'Attendance',   items: navigation.filter(n => ['Student Attendance','Staff Attendance','Teacher Attendance','Biometric','Student Gate Log'].includes(n.name)) },
  { label: 'Salary & Leaves', items: navigation.filter(n => ['Staff Salary','Staff Leaves','Staff Management (Non-Portal)','Holidays','Salary Records','Leaves'].includes(n.name)) },
  { label: 'Finance',      items: navigation.filter(n => ['Fees','Expenses','Assets'].includes(n.name)) },
  { label: 'Content',      items: navigation.filter(n => ['Materials','Certificates','Notices','Feedback','Access Panel'].includes(n.name)) },
  { label: 'Requests',     items: navigation.filter(n => ['Coordinator Requests','Finance Requests'].includes(n.name)) },
  { label: 'Settings',     items: navigation.filter(n => ['Settings'].includes(n.name)) },
  { label: 'Account',      items: navigation.filter(n => ['My Attendance','My Salary','My Leaves'].includes(n.name)) },
];

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .sidebar { width:240px; min-width:240px; min-height:100vh; background:#0f1123; display:flex; flex-direction:column; overflow-x:hidden; font-family:'Sora',sans-serif; border-right:1px solid rgba(255,255,255,.04); }
  .sidebar-brand { padding:20px 20px 16px; border-bottom:1px solid rgba(255,255,255,.06); flex-shrink:0; position:relative; }
  .sidebar-brand-row { display:flex; align-items:center; gap:10px; }
  .sidebar-brand-icon { width:34px; height:34px; background:#3b6ef8; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .sidebar-brand-icon svg { width:18px; height:18px; fill:none; stroke:white; stroke-width:2; }
  .sidebar-brand-name { font-size:.95rem; font-weight:700; color:#fff; letter-spacing:-.02em; line-height:1; }
  .sidebar-brand-sub  { font-size:.68rem; color:#4a5075; margin-top:3px; letter-spacing:.04em; text-transform:uppercase; }
  .sidebar-nav { flex:1; padding:10px; overflow-y:auto; overflow-x:hidden; scrollbar-width:none; }
  .sidebar-nav::-webkit-scrollbar { display:none; }
  .sidebar-section-label { font-size:.60rem; font-weight:600; color:#353b5c; text-transform:uppercase; letter-spacing:.08em; padding:10px 8px 4px; }
  .sidebar-link { display:flex; align-items:center; gap:10px; padding:8px 10px; border-radius:10px; text-decoration:none; color:#7880a4; font-size:.81rem; font-weight:500; transition:background .15s,color .15s; margin-bottom:1px; position:relative; }
  .sidebar-link-text { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .sidebar-link:hover { background:rgba(255,255,255,.04); color:#c8ccde; }
  .sidebar-link.active { background:rgba(59,110,248,.12); color:#3b6ef8; }
  .sidebar-link.active::before { content:''; position:absolute; left:-10px; top:25%; bottom:25%; width:3px; background:#3b6ef8; border-radius:0 3px 3px 0; }
  .sidebar-link svg { width:17px; height:17px; stroke-width:1.7; flex-shrink:0; }
  .sidebar-footer { padding:14px 12px; border-top:1px solid rgba(255,255,255,.05); flex-shrink:0; }
  .sidebar-footer-text { font-size:.68rem; color:#353b5c; text-align:center; }
  .sidebar-close { display:none; }
  @media (max-width:768px) {
    .sidebar-close { display:flex; align-items:center; justify-content:center; position:absolute; top:16px; right:16px; width:28px; height:28px; border-radius:8px; background:rgba(255,255,255,.08); border:none; cursor:pointer; }
    .sidebar-close svg { width:15px; height:15px; stroke:white; stroke-width:2; }
  }
  .notice-badge { margin-left:auto; min-width:18px; height:18px; padding:0 5px; border-radius:99px; background:#ef4444; color:white; font-size:.6rem; font-weight:700; display:flex; align-items:center; justify-content:center; line-height:1; flex-shrink:0; }
`;

const Sidebar = ({ onClose }) => {
  const location                        = useLocation();
  const { brand }                       = useSchoolBrand();
  const { libraryEnabled, hostelEnabled, cloPloEnabled } = useSchoolModules();
  const [noticeCount,  setNoticeCount]  = useState(0);
  const [reqCount,     setReqCount]     = useState(0);
  const [financeCount, setFinanceCount] = useState(0);

  // Library/Hostel nav only appears once Super Admin has enabled the module
  // for this school — otherwise the sidebar is unchanged from before.
  // CLO/PLO (OBE) nav is gated the same way, on the Super Admin cloPloEnabled switch.
  const moduleSections = sections.map(sec => {
    if (sec.label === 'Academic') {
      return { ...sec, items: [
        ...sec.items,
        ...(libraryEnabled ? [{ name: 'Librarians',     href: '/librarians',     icon: BookOpenIcon }] : []),
        ...(hostelEnabled  ? [{ name: 'Hostel Members', href: '/hostel-members', icon: HomeModernIcon }] : []),
      ] };
    }
    if (sec.label === 'Finance') {
      return { ...sec, items: [
        ...sec.items,
        ...(libraryEnabled ? [{ name: 'Library', href: '/library', icon: BookOpenIcon }] : []),
        ...(hostelEnabled  ? [{ name: 'Hostel',  href: '/hostel-overview', icon: HomeModernIcon }] : []),
      ] };
    }
    if (sec.label === 'OBE') {
      return { ...sec, items: cloPloEnabled ? sec.items : [] };
    }
    return sec;
  }).filter(sec => sec.items.length > 0);

  useEffect(() => {
    api.get('/admin/notices/unread-count').then(r => setNoticeCount(r.data?.count || 0)).catch(() => {});
    api.get('/admin/coordinator-requests/counts').then(r => setReqCount(r.data?.pending || 0)).catch(() => {});
    api.get('/admin/finance-requests/counts').then(r => setFinanceCount(r.data?.pending || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/coordinator-requests')) setReqCount(0);
    if (location.pathname.startsWith('/finance-requests'))     setFinanceCount(0);
    if (location.pathname.startsWith('/notices')) {
      api.post('/admin/notices/mark-read').catch(() => {});
      setNoticeCount(0);
    }
  }, [location.pathname]);

  const getBadge = (item) => {
    if (!item.badge) return null;
    let count = 0;
    if (item.badge === 'notices')     count = noticeCount;
    if (item.badge === 'coordinator') count = reqCount;
    if (item.badge === 'finance')     count = financeCount;
    if (!count) return null;
    return <span className="notice-badge">{count > 99 ? '99+' : count}</span>;
  };

  return (
    <>
      <style>{style}</style>
      <div className="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-row">
            {brand.logo
              ? <img src={brand.logo} alt="logo" style={{width:34,height:34,borderRadius:10,objectFit:'contain',background:'rgba(255,255,255,.95)',padding:2,border:'1px solid rgba(255,255,255,.12)',flexShrink:0}}/>
              : <div className="sidebar-brand-icon">
                  <svg viewBox="0 0 24 24"><path d="M12 3L2 9l10 6 10-6-10-6zM2 15l10 6 10-6M2 12l10 6 10-6" /></svg>
                </div>
            }
            <div>
              <div className="sidebar-brand-name">{brand.name || 'School Portal'}</div>
              <div className="sidebar-brand-sub">Admin Dashboard</div>
            </div>
          </div>
          {onClose && <button className="sidebar-close" onClick={onClose}><XMarkIcon /></button>}
        </div>

        <nav className="sidebar-nav">
          {moduleSections.map(sec => (
            <div key={sec.label}>
              <div className="sidebar-section-label">{sec.label}</div>
              {sec.items.map(item => {
                const isActive = location.pathname === item.href ||
                  location.pathname.startsWith(item.href + '/');
                return (
                  <Link key={item.name} to={item.href}
                    className={`sidebar-link${isActive ? ' active' : ''}`}
                    onClick={onClose}>
                    <item.icon />
                    <span className="sidebar-link-text">{item.name}</span>
                    {getBadge(item)}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-footer-text">v1.0.0 · School Portal</div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;